import { Button, Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import InputMoney from "../../../components/Forms/Element/InputMoney";
import InputDecimal from "../../../components/Forms/Element/InputDecimal";

/**
 * Tabela de faixas da comissão escalonada.
 *
 * Espaçamento seguindo a convenção do formulário: `mb-3` separa blocos, e legenda colada ao
 * controle de cima usa `mt-2`. Antes daqui saíam `mb-2` e `mt-2` soltos, e a tabela ficava com
 * um respiro diferente do resto da tela.
 *
 * Cada linha pede só o valor de entrada e o percentual. Não existe campo "até": o fim de uma
 * faixa é sempre o começo da seguinte, e pedir as duas pontas deixaria o operador cadastrar
 * tabela com buraco no meio (uma faixa terminando em 5.000 e a próxima começando em 6.000) ou
 * com sobreposição, que são justamente os erros que ele não teria como perceber depois.
 *
 * A primeira linha fica travada em zero pelo mesmo motivo: é ela que garante que todo mês,
 * por pior que seja, tenha um percentual.
 */
const TierEditor = ({ tiers, onChange, error }) => {
    const { t } = useTranslation();

    const updateTier = (index, field, value) => {
        const updated = tiers.map((tier, position) => (
            position === index ? { ...tier, [field]: value } : tier
        ));
        onChange(updated);
    };

    const addTier = () => onChange([...tiers, { minRevenue: '', percent: '' }]);

    const removeTier = (index) => onChange(tiers.filter((_, position) => position !== index));

    return (
        <>
            {/* O aviso explica a regra que mais gera dúvida: a faixa alcançada vale para o
                faturamento inteiro do mês, e não só para o que passou da entrada.
                Não diz o que entra na soma porque a faixa só existe em "somente produtos", e
                isso já está escrito no campo de abrangência logo acima. */}
            <div className="alert alert-info d-flex align-items-start mb-3" role="alert">
                <i className="fa-solid fa-circle-info me-2 mt-1" aria-hidden="true"></i>
                <span>{t('commission_tier_hint')}</span>
            </div>

            {tiers.map((tier, index) => (
                // g-3 e as colunas md mantêm a linha legível no celular, onde ela empilha, e
                // repetem o espaçamento usado no resto do formulário.
                <Row className="g-3 align-items-end mb-3" key={index}>
                    <Col md={5} xs={6}>
                        <FormGroup className="mb-0">
                            <FormLabel>{t('commission_tier_from')}</FormLabel>
                            <InputMoney
                                currency
                                value={tier.minRevenue}
                                onChange={(value) => updateTier(index, 'minRevenue', value)}
                                disabled={index === 0}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4} xs={4}>
                        <FormGroup className="mb-0">
                            <FormLabel>{t('commission')} (%)</FormLabel>
                            <InputDecimal
                                value={tier.percent}
                                onValueChange={(values) => updateTier(index, 'percent', values.floatValue ?? '')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={3} xs={2}>
                        {/* A primeira faixa não pode ser removida: sem ela a tabela deixa de
                            cobrir o faturamento baixo. */}
                        {/* Mesmo botão de remover linha do resto do sistema (plano de metas e
                            itens da venda): danger sólido no tamanho sm. O "danger light" que
                            estava aqui é a variante translúcida, usada em badge, e no tamanho
                            padrão ainda ficava mais alto que os campos ao lado. */}
                        {index > 0 && (
                            <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                title={t('remove')}
                                aria-label={t('remove')}
                                onClick={() => removeTier(index)}
                            >
                                <i className="fa fa-trash"></i>
                            </Button>
                        )}
                    </Col>
                </Row>
            ))}

            <FieldError error={error} />

            {/* Mesmo par do plano de metas: primary sólido para adicionar linha. */}
            <Button type="button" variant="primary" onClick={addTier}>
                <i className="fa fa-plus me-2"></i>
                {t('commission_tier_add')}
            </Button>
        </>
    );
};

/** A validação da tabela é do conjunto, não de um campo, então não cabe sob um input. */
const FieldError = ({ error }) => (
    typeof error === 'string' && error ? <div className="text-danger fs-12 mb-3">{error}</div> : null
);

export default TierEditor;
